import { TFunction } from "i18next";
import prettyBytes from "pretty-bytes";

import { DocumentKind, FileValidationResult } from "../types";

/** Keep this allow-list aligned with the document translation endpoint. */
export const getFileKind = (filename?: string): DocumentKind => {
  const name = filename?.toLowerCase() ?? "";
  if (name.endsWith(".docx")) return "word";
  if (name.endsWith(".pptx")) return "powerpoint";
  return null;
};

/** Returns an i18n key; callers render it with `t()`. */
export const getDocumentTypeLabel = (kind: DocumentKind): string => {
  switch (kind) {
    case "word":
      return "translation.documentType.word";
    case "powerpoint":
      return "translation.documentType.powerpoint";
    default:
      return "translation.documentType.document";
  }
};

export const validateFile = (
  file: File,
  maxSizeMB: number,
  t: TFunction,
): FileValidationResult => {
  if (getFileKind(file?.name) === null) {
    return {
      valid: false,
      error: t("translation.errors.unsupportedFile"),
    };
  }

  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return {
      valid: false,
      error: t("translation.errors.fileTooLarge", { size: maxSizeMB }),
    };
  }

  return { valid: true };
};

/**
 * Formats file size in human-readable format
 * Uses pretty-bytes library for consistent formatting
 */
export const formatFileSize = (
  bytes: number | undefined,
  t: TFunction,
): string => {
  if (!bytes) return t("translation.unknownSize");
  return prettyBytes(bytes);
};

/**
 * Sanitizes filename to prevent XSS and path traversal
 * Removes potentially dangerous characters
 */
export const sanitizeFilename = (filename: string): string => {
  // Remove Windows-invalid chars and control characters
  return (
    filename
      .replace(/[<>:"/\\|?*]/g, "") // Remove special chars
      // eslint-disable-next-line no-control-regex
      .replace(/[\x00-\x1F\x7F]/g, "") // Remove control chars
      .replace(/^\.+/, "") // Remove leading dots
      .trim()
  );
};

/** Appends a normalized language suffix before the original .docx/.pptx extension. */
export const generateTranslatedFilename = (
  originalFilename: string,
  language: string,
): string => {
  const sanitized = sanitizeFilename(originalFilename);
  const extMatch = sanitized.match(/\.(docx|pptx)$/i);
  const ext = extMatch ? extMatch[0].toLowerCase() : "";
  const nameWithoutExt = ext
    ? sanitized.slice(0, sanitized.length - ext.length)
    : sanitized;
  const languageSuffix = language.toLowerCase().replace(/\s+/g, "_");
  return `${nameWithoutExt}_${languageSuffix}${ext}`;
};
